import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/lib/cn";

const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
  {
    variants: {
      variant: {
        neutral: "bg-surface-sunken text-copy-muted",
        primary: "bg-primary-light text-primary",
        success: "bg-success-light text-success-foreground",
        warning: "bg-warning-light text-warning-foreground",
        destructive: "bg-destructive-light text-destructive-foreground",
        info: "bg-info-light text-info-foreground",
        outline: "border border-border-strong text-copy-muted",
        solid: "bg-primary text-primary-foreground",
      },
    },
    defaultVariants: {
      variant: "neutral",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean;
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, dot, children, ...props }, ref) => (
    <span ref={ref} className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot && (
        <span
          className={cn(
            "h-1.5 w-1.5 rounded-full",
            variant === "success" && "bg-success",
            variant === "warning" && "bg-warning",
            variant === "destructive" && "bg-destructive",
            variant === "info" && "bg-info",
            variant === "primary" && "bg-primary",
            (!variant || variant === "neutral" || variant === "outline" || variant === "solid") &&
              "bg-current",
          )}
        />
      )}
      {children}
    </span>
  ),
);
Badge.displayName = "Badge";

export { badgeVariants };
